/**
 * NfsePage — porta de entrada do módulo de NFS-e.
 *
 * Portões compostos no call-site (como FinancePage):
 *   A) plano   → useFeature('nfse_emission')
 *   B) papel   → useRole().canSeeFinance (owner/admin)
 *   C) contribuinte → taxpayer_type === 'autonomo_informal' esconde o módulo
 *   D) readiness → useFiscalReadiness().ready (CNPJ + município + cert válido).
 *      Se falta algo, mostra o NfseReadinessGate (o que falta + "Configurar
 *      empresa" abrindo o cadastro na seção fiscal) — substitui o antigo wizard.
 *
 * A ORDEM entre papel e plano é regra de produto (#38) e tem teste próprio:
 * mostrar upsell a um `member` seria mentira dupla — sugere que o problema dele
 * é dinheiro e que assinar resolveria, e não resolve.
 *
 * ─── O DESENHO OFICIAL NUMA TELA DE SEIS ESTADOS ────────────────────────────
 *
 * Hero sangrado + bilhete, o mesmo de `/quotes` e `/recibos`, mas aqui ele é um
 * COMPONENTE (`NfseHero`) e não marcação inline: são seis estados de tela mais o
 * módulo de emissão, que vive em outro arquivo. O motivo está no cabeçalho dele,
 * junto com a regra que decide quando o valor ao vivo aparece — nos portões ele
 * NÃO aparece, porque um "R$ 0,00" ali afirmaria que o prestador não faturou
 * nada quando o que houve foi uma recusa (#187).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Crown, FileText, Loader2, Lock, ReceiptText } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useFeature } from '@/hooks/useFeature';
import { useRole } from '@/hooks/useRole';
import { useFiscalProfile } from '@/hooks/useFiscalProfile';
import { useFiscalReadiness } from '@/hooks/useFiscalReadiness';
import { NfseReadinessGate } from '@/components/nfse/NfseReadinessGate';
import { NfseHero } from '@/components/nfse/NfseHero';
import { CompanyFormModal } from '@/components/company/CompanyFormModal';
import { NfseModule } from '@/components/nfse/NfseModule';
import { CONTENT_CARD } from '@/components/ui/field';
import { cn } from '@/lib/utils';

/**
 * O molde de tela dos PORTÕES: hero sem valor + um cartão explicando por quê.
 *
 * Ele existe para os seis estados não divergirem entre si — o gate de papel e o
 * de plano são a mesma tela com outro texto, e escrevê-los soltos já produziu
 * cabeçalhos diferentes na mesma rota antes.
 */
function NfseGateLayout({
  onBack,
  children,
}: {
  onBack: () => void;
  children: React.ReactNode;
}) {
  return (
    <MainLayout>
      <div className="pb-4">
        <NfseHero onBack={onBack} />
        {/* Conteúdo — sobe e encosta na curva do hero, como em /quotes. */}
        <div className="relative z-10 -mt-9 space-y-5">{children}</div>
      </div>
    </MainLayout>
  );
}

function GateCard({ children }: { children: React.ReactNode }) {
  return (
    <div className={cn(CONTENT_CARD, 'flex flex-col items-center justify-center p-6 text-center')}>
      {children}
    </div>
  );
}

export default function NfsePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const { isOnline } = useNetworkStatus();
  const nfse = useFeature('nfse_emission');
  const { canSeeFinance } = useRole();
  const { profile } = useFiscalProfile();
  const readiness = useFiscalReadiness();
  const qc = useQueryClient();
  const [configOpen, setConfigOpen] = useState(false);

  // Modal de cadastro da empresa (seção fiscal), compartilhado pelos portões C e D.
  // O CompanyFormModal grava o perfil fiscal direto no banco (fora do cache do
  // useFiscalProfile), então ao concluir invalidamos as queries fiscais para o
  // readiness recalcular e a página avançar.
  const configModal = (
    <CompanyFormModal
      open={configOpen}
      onOpenChange={setConfigOpen}
      company={activeCompany ?? null}
      onSuccess={() => {
        qc.invalidateQueries({ queryKey: ['fiscal-profile', activeCompany?.id] });
        qc.invalidateQueries({ queryKey: ['fiscal-active-cert', activeCompany?.id] });
      }}
    />
  );

  // Não autenticado
  if (!user) {
    return (
      <NfseGateLayout onBack={() => navigate('/dashboard')}>
        <GateCard>
          <FileText className="mb-4 h-12 w-12 text-muted-foreground/50" />
          <p className="text-muted-foreground">Faça login para acessar a NFS-e</p>
          <Button3D className="mt-4" onClick={() => navigate('/auth')}>Entrar</Button3D>
        </GateCard>
      </NfseGateLayout>
    );
  }

  // Portão B: papel (owner/admin). Membros/viewers não acessam.
  if (!canSeeFinance) {
    return (
      <NfseGateLayout onBack={() => navigate('/dashboard')}>
        <GateCard>
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted">
            <Lock className="h-7 w-7 text-muted-foreground" />
          </div>
          <h2 className="text-lg font-semibold">Acesso restrito</h2>
          <p className="mt-1 max-w-xs text-sm text-muted-foreground">
            A NFS-e está disponível apenas para o proprietário e administradores desta empresa.
          </p>
          <Button variant="outline" className="mt-5" onClick={() => navigate('/dashboard')}>
            Voltar ao início
          </Button>
        </GateCard>
      </NfseGateLayout>
    );
  }

  // Portão A: plano.
  if (!nfse.allowed) {
    return (
      <NfseGateLayout onBack={() => navigate(-1)}>
        <GateCard>
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <Lock className="h-7 w-7 text-primary" />
          </div>
          <h2 className="text-lg font-semibold">Emissão de NFS-e</h2>
          <p className="mt-1 max-w-xs text-sm text-muted-foreground">
            Emita notas fiscais de serviço direto do Orçamigo. Disponível nos planos pagos.
          </p>
          <Button3D color="amber" className="mt-5" onClick={() => nfse.require()}>
            <Crown className="mr-2 h-4 w-4" />
            Desbloquear
          </Button3D>
        </GateCard>
      </NfseGateLayout>
    );
  }

  if (readiness.isLoading) {
    return (
      <NfseGateLayout onBack={() => navigate(-1)}>
        <div className="flex justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </NfseGateLayout>
    );
  }

  // Portão C: autônomo informal → NFS-e não se aplica; oferece Recibo.
  if (profile?.taxpayer_type === 'autonomo_informal') {
    return (
      <NfseGateLayout onBack={() => navigate(-1)}>
        <GateCard>
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted">
            <ReceiptText className="h-7 w-7 text-muted-foreground" />
          </div>
          <h2 className="text-lg font-semibold">NFS-e não se aplica</h2>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            Como autônomo sem CNPJ, você não emite NFS-e. Use o Recibo para comprovar seus
            serviços — ele não é um documento fiscal, mas serve como comprovante.
          </p>
          <Button variant="outline" className="mt-5" onClick={() => navigate('/dashboard')}>
            Voltar ao início
          </Button>
          {/* Autônomo que virou MEI: caminho para reconfigurar o regime aqui mesmo. */}
          <button
            type="button"
            onClick={() => setConfigOpen(true)}
            disabled={!isOnline}
            className="mt-4 text-sm text-primary hover:underline disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
          >
            Já tem CNPJ? Configure sua empresa como MEI
          </button>
          {!isOnline && (
            <p className="mt-1 text-xs text-muted-foreground">Conecte-se à internet para configurar.</p>
          )}
        </GateCard>
        {configModal}
      </NfseGateLayout>
    );
  }

  // Portão D: readiness. Falta CNPJ/município/certificado → tela que aponta o que
  // falta e leva ao cadastro da empresa (seção fiscal). Substitui o wizard.
  if (!readiness.ready || !profile) {
    return (
      <NfseGateLayout onBack={() => navigate(-1)}>
        <NfseReadinessGate
          missing={readiness.missing}
          onConfigure={() => setConfigOpen(true)}
          offline={!isOnline}
        />
        {configModal}
      </NfseGateLayout>
    );
  }

  // Pronto → módulo de emissão (hero com valor ao vivo + lista + nova NFS-e).
  return (
    <MainLayout>
      <NfseModule profile={profile} onBack={() => navigate(-1)} />
    </MainLayout>
  );
}
